import { useRef } from 'react';

import { CurrencyInput } from '@playbooks/ui/components/currency-input';
import { GoogleAutocomplete } from '@playbooks/ui/components/google-autocomplete';
import { Input, inputClassName } from '@playbooks/ui/components/input';
import { useElementKeyDown } from '@playbooks/ui/hooks';
import { cn } from '@playbooks/ui/lib/utils';

import type {
	FormCheckboxProps,
	FormFileProps,
	FormInputCurrencyProps,
	FormInputLocationProps,
	FormInputProps,
	FormSelectProps,
} from '../types';

export const FormCheckbox = ({ className, size: _size, variant: _variant, ...props }: FormCheckboxProps) => {
	// Theme
	const rootClassName = cn(
		'form-checkbox size-4 shrink-0 cursor-pointer appearance-none rounded-control border border-border-strong bg-surface-control outline-none transition-colors focus-visible:border-focus focus-visible:ring-3 focus-visible:ring-focus/50 disabled:pointer-events-none disabled:opacity-50',
		className,
	);

	// Render
	return <input type='checkbox' {...props} className={rootClassName} />;
};
export const FormCurrencyInput = ({
	id,
	value,
	prefix,
	placeholder,
	onChange,
	readOnly,
	className,
}: FormInputCurrencyProps) => {
	// Theme
	const rootClassName = cn(inputClassName, className);

	// Render
	return (
		<CurrencyInput
			id={id}
			value={value}
			prefix={prefix}
			placeholder={placeholder}
			onChange={onChange}
			readOnly={readOnly}
			className={rootClassName}
		/>
	);
};
export const FormDivInput = ({ placeholder, className, children, ...props }: FormInputProps) => {
	// Theme
	const rootClassName = cn(inputClassName, 'flex items-center overflow-x-auto', className);

	// Render
	return (
		<div tabIndex={0} {...props} className={rootClassName}>
			{children || <span className='text-foreground-body'>{placeholder}</span>}
		</div>
	);
};
export const FormFileInput = ({ name: _name, size: _size, variant: _variant, className, ...props }: FormFileProps) => (
	<Input {...props} className={className} />
);
export const FormLocationInput = ({
	options,
	googleMapsApiKey,
	onSelect,
	name: _name,
	size: _size,
	variant: _variant,
	className,
	...props
}: FormInputLocationProps) => {
	const ref = useRef(null);
	useElementKeyDown(
		ref.current,
		event => {
			if (event.key === 'Enter') event.preventDefault();
		},
		[],
	);

	// Render
	return (
		<GoogleAutocomplete options={options} googleMapsApiKey={googleMapsApiKey} onSelect={onSelect}>
			<Input ref={ref} {...props} className={className} />
		</GoogleAutocomplete>
	);
};
export const FormSelect = ({
	name: _name,
	size: _size,
	variant: _variant,
	options = [],
	placeholder,
	className,
	...props
}: FormSelectProps) => {
	// Theme
	const rootClassName = cn(inputClassName, 'cursor-pointer', className);

	// Render
	return (
		<select {...props} className={rootClassName}>
			<option value='' disabled>
				{placeholder}
			</option>
			{options.map((option, index) => (
				<option key={index} value={option}>
					{option}
				</option>
			))}
		</select>
	);
};
